import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardFooter,
  InteractiveCard,
} from "../../../styles/components/common/cards";
import {
  Button,
  SecondaryButton,
} from "../../../styles/components/common/buttons";
import styled from "styled-components";
import { PageHeader, PageTitle } from "../../../styles/components";

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const AssessmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 2rem;
  padding: 1rem 0;
`;

const SkillTag = styled.span`
  background: rgba(99, 102, 241, 0.15);
  color: rgba(255, 255, 255, 0.9);
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.875rem;
  font-weight: 500;
  height: 24px;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
`;

const AssessmentDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.75rem;
  height: 55px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.5;
  font-size: 0.9rem;
`;

const QuestionOption = styled.label`
  display: flex;
  align-items: center;
  padding: 1rem;
  border: ${({ selected, theme }) =>
    selected
      ? `2px solid ${theme.colors.primary || "#2196F3"}`
      : "1px solid #ddd"};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ selected }) =>
    selected ? "rgba(33,150,243,0.08)" : "transparent"};

  input[type="radio"] {
    margin: 0;
    margin-right: 1rem;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

const ScoreText = styled.div`
  font-size: 2rem;
  color: ${({ score, theme }) =>
    score >= 80
      ? theme.colors.success || "#4CAF50"
      : theme.colors.primary || "#2196F3"};
  margin: 1.5rem 0;
  text-align: center;
`;

const AssessmentContainer = styled(InteractiveCard)`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  height: 400px;
  box-sizing: border-box;

  &:hover {
    border-color: rgba(99, 102, 241, 0.4);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.1);
    transform: translateY(-2px);
  }
`;

const AssessmentTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 0.75rem;
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AssessmentInfo = styled.div`
  margin-bottom: 0.75rem;
  flex: 0 0 auto;
  min-height: 80px;
`;

const SkillsSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0;
  min-height: 30px;
  max-height: 60px;
  overflow: hidden;
`;

const AssessmentFooter = styled.div`
  margin-top: auto;
  padding-top: 0.5rem;
  text-align: center;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;

  span:first-child {
    margin-right: 0.5rem;
  }
`;

const PreviousScore = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: ${(props) => (props.score >= 80 ? "#4CAF50" : "#2196F3")};
`;

const LastTaken = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  text-align: center;
`;

const mockQuestions = {
  cs: [
    {
      id: 1,
      question: "What is the primary purpose of React's virtual DOM?",
      options: [
        "To directly manipulate the browser's DOM",
        "To improve performance by minimizing actual DOM updates",
        "To handle server-side rendering",
        "To manage component lifecycle methods",
      ],
      correctAnswer: 1,
      explanation:
        "The virtual DOM helps React optimize performance by batching and minimizing actual DOM updates.",
    },
    {
      id: 2,
      question: "Which HTTP status code indicates a successful request?",
      options: [
        "200 OK",
        "404 Not Found",
        "500 Internal Server Error",
        "301 Moved Permanently",
      ],
      correctAnswer: 0,
    },
    {
      id: 3,
      question: "What is the time complexity of a binary search algorithm?",
      options: ["O(n)", "O(n²)", "O(log n)", "O(1)"],
      correctAnswer: 2,
    },
    {
      id: 4,
      question: "Which of the following is NOT a JavaScript data type?",
      options: ["undefined", "boolean", "char", "symbol"],
      correctAnswer: 2,
    },
    {
      id: 5,
      question: "What is the purpose of the 'key' prop in React lists?",
      options: [
        "To style list items",
        "To help React track list items efficiently",
        "To make the list sortable",
        "To set the item's visibility",
      ],
      correctAnswer: 1,
    },
  ],
  marketing: [
    {
      id: 1,
      question: "What is a sales funnel?",
      options: [
        "A physical funnel used in retail stores",
        "A visual representation of the customer journey",
        "A marketing budget allocation tool",
        "A type of social media platform",
      ],
      correctAnswer: 1,
      explanation:
        "A sales funnel visualizes the stages a customer goes through before making a purchase.",
    },
    {
      id: 2,
      question:
        "Which metric measures the percentage of website visitors who convert into customers?",
      options: [
        "Bounce Rate",
        "Conversion Rate",
        "Click-through Rate",
        "Page Views",
      ],
      correctAnswer: 1,
      explanation:
        "Conversion rate measures the percentage of visitors who complete a desired action.",
    },
    {
      id: 3,
      question: "What is market segmentation?",
      options: [
        "Dividing products into categories",
        "Breaking down the market into distinct groups",
        "Analyzing competitors",
        "Setting product prices",
      ],
      correctAnswer: 1,
      explanation:
        "Market segmentation involves dividing a market into distinct groups with similar needs.",
    },
    {
      id: 4,
      question: "What does ROI stand for in marketing?",
      options: [
        "Rate Of Interest",
        "Return On Investment",
        "Range Of Impact",
        "Risk Of Implementation",
      ],
      correctAnswer: 1,
      explanation:
        "ROI (Return On Investment) measures the profitability of marketing investments.",
    },
    {
      id: 5,
      question: "Which social media platform is best for B2B marketing?",
      options: ["TikTok", "LinkedIn", "Instagram", "Snapchat"],
      correctAnswer: 1,
      explanation:
        "LinkedIn is the most effective platform for B2B marketing due to its professional focus.",
    },
  ],
  business: [
    {
      id: 1,
      question: "What is a SWOT analysis used for?",
      options: [
        "Software testing",
        "Evaluating business position and strategy",
        "Employee performance review",
        "Financial auditing",
      ],
      correctAnswer: 1,
      explanation:
        "SWOT analysis evaluates Strengths, Weaknesses, Opportunities, and Threats.",
    },
    {
      id: 2,
      question: "What is the purpose of a business model canvas?",
      options: [
        "To design logos",
        "To visualize business structure and strategy",
        "To track employee attendance",
        "To manage inventory",
      ],
      correctAnswer: 1,
      explanation:
        "The business model canvas helps visualize and develop business models.",
    },
    {
      id: 3,
      question: "What does MVP stand for in business context?",
      options: [
        "Most Valuable Player",
        "Minimum Viable Product",
        "Maximum Value Proposition",
        "Market Value Point",
      ],
      correctAnswer: 1,
      explanation:
        "MVP represents the simplest version of a product that can be released.",
    },
    {
      id: 4,
      question: "What is the primary goal of change management?",
      options: [
        "To reduce costs",
        "To guide organizations through transitions",
        "To increase sales",
        "To hire new employees",
      ],
      correctAnswer: 1,
      explanation:
        "Change management helps organizations transition smoothly through changes.",
    },
    {
      id: 5,
      question: "What is a key component of strategic planning?",
      options: [
        "Daily scheduling",
        "Vision and mission statements",
        "Employee training",
        "Product pricing",
      ],
      correctAnswer: 1,
      explanation:
        "Vision and mission statements guide strategic planning and decision-making.",
    },
  ],
};

const mockAssessments = [
  {
    id: 1,
    type: "cs",
    title: "CS Workshop Online Assessment",
    description:
      "Test your knowledge in web development, algorithms, and system design.",
    duration: "30 mins",
    totalQuestions: 5,
    skills: ["Web Development", "Algorithms", "System Design"],
    questions: mockQuestions.cs,
    status: "available",
  },
  {
    id: 2,
    type: "marketing",
    title: "Digital Marketing Fundamentals",
    description:
      "Assess your understanding of modern marketing concepts and strategies.",
    duration: "25 mins",
    totalQuestions: 5,
    skills: ["Social Media", "Analytics", "Content Strategy"],
    questions: mockQuestions.marketing,
    status: "available",
  },
  {
    id: 3,
    type: "business",
    title: "Business Strategy Assessment",
    description:
      "Evaluate your knowledge of business strategy and management principles.",
    duration: "35 mins",
    totalQuestions: 5,
    skills: ["Strategic Planning", "Business Models", "Change Management"],
    questions: mockQuestions.business,
    status: "available",
  },
];

const AssessmentCenter = () => {
  const [assessments] = useState(mockAssessments);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [currentScore, setCurrentScore] = useState(null);
  const [isScorePublic, setIsScorePublic] = useState(false);
  const [temporaryScores, setTemporaryScores] = useState({});
  const [showAnswers, setShowAnswers] = useState(false);

  const startAssessment = (assessment) => {
    setSelectedAssessment(assessment);
    setCurrentQuestion(0);
    setUserAnswers({});
    setShowResults(false);
    setShowAnswers(false);
  };

  const handleAnswerSelect = (questionId, answerIndex) => {
    setUserAnswers((prev) => ({
      ...prev,
      [questionId]: answerIndex,
    }));
  };

  const calculateScore = () => {
    const questions = selectedAssessment.questions;
    let correct = 0;
    questions.forEach((question) => {
      if (userAnswers[question.id] === question.correctAnswer) {
        correct++;
      }
    });
    return {
      score: Math.round((correct / questions.length) * 100),
      correctCount: correct,
      totalQuestions: questions.length,
    };
  };
  const submitAssessment = () => {
    const result = calculateScore();
    setCurrentScore(result);
    setShowResults(true);
    // Store score in temporary state
    setTemporaryScores((prev) => ({
      ...prev,
      [selectedAssessment.id]: {
        assessmentId: selectedAssessment.id,
        title: selectedAssessment.title,
        score: result.score,
        correctCount: result.correctCount,
        totalQuestions: result.totalQuestions,
        date: new Date().toISOString(),
        answers: userAnswers,
        isScorePublic: isScorePublic,
      },
    }));
  };

  const renderQuestion = (question) => (
    <Card style={{ marginBottom: "1rem" }}>
      <h3 style={{ marginBottom: "1.5rem" }}>{question.question}</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {question.options.map((option, index) => (
          <QuestionOption
            key={index}
            selected={userAnswers[question.id] === index}
          >
            <input
              type="radio"
              name={`question-${question.id}`}
              checked={userAnswers[question.id] === index}
              onChange={() => handleAnswerSelect(question.id, index)}
            />
            <span style={{ flex: 1 }}>{option}</span>
          </QuestionOption>
        ))}
      </div>
    </Card>
  );

  if (showResults) {
    return (
      <Container>
        <Card style={{ maxWidth: "800px", margin: "2rem auto" }}>
          <h2 style={{ marginBottom: "1.5rem", textAlign: "center" }}>
            Assessment Complete!
          </h2>
          <ScoreText score={currentScore.score}>
            Score: {currentScore.correctCount}/{currentScore.totalQuestions} (
            {currentScore.score}%)
          </ScoreText>
          <div style={{ marginBottom: "2rem", textAlign: "center" }}>
            {currentScore.score >= 80 ? (
              <p style={{ color: "#4CAF50" }}>
                Excellent work! You've demonstrated strong understanding of the
                material.
              </p>
            ) : currentScore.score >= 60 ? (
              <p style={{ color: "#2196F3" }}>
                Good effort! Review the topics you missed to improve your
                understanding.
              </p>
            ) : (
              <p style={{ color: "#f44336" }}>
                Keep practicing! Focus on the areas where you need improvement.
              </p>
            )}
          </div>
          <div style={{ marginBottom: "2rem" }}>
            <Button
              onClick={() => setShowAnswers(!showAnswers)}
              style={{ width: "100%", marginBottom: "1rem" }}
            >
              {showAnswers ? "Hide Answers" : "Review Answers"}
            </Button>

            {showAnswers && (
              <div style={{ marginTop: "2rem" }}>
                {selectedAssessment.questions.map((question, idx) => (
                  <Card
                    key={idx}
                    style={{
                      padding: "1.5rem",
                      marginBottom: "1rem",
                      border:
                        userAnswers[question.id] === question.correctAnswer
                          ? "2px solid #4CAF50"
                          : "2px solid #f44336",
                    }}
                  >
                    <h4 style={{ marginBottom: "1rem" }}>
                      {question.question}
                    </h4>
                    <div style={{ marginBottom: "1rem" }}>
                      <p style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                        Your answer:{" "}
                        {question.options[userAnswers[question.id]]}
                      </p>
                      <p style={{ color: "#4CAF50" }}>
                        Correct answer:{" "}
                        {question.options[question.correctAnswer]}
                      </p>
                    </div>
                    <p
                      style={{
                        fontSize: "0.9rem",
                        color: "rgba(255, 255, 255, 0.7)",
                        fontStyle: "italic",
                        marginTop: "0.5rem",
                      }}
                    >
                      {question.explanation}
                    </p>
                  </Card>
                ))}
              </div>
            )}
          </div>{" "}
          <div style={{ marginTop: "2rem", textAlign: "center" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "1rem",
                gap: "0.5rem",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={isScorePublic}
                onChange={() => setIsScorePublic(!isScorePublic)}
                style={{
                  width: "18px",
                  height: "18px",
                  margin: 0,
                  cursor: "pointer",
                }}
              />
              Make this score visible on my profile
            </label>

            <SecondaryButton
              onClick={() => {
                setShowResults(false);
                setSelectedAssessment(null);
                setUserAnswers({});
              }}
            >
              Back to Assessments
            </SecondaryButton>
          </div>
        </Card>
      </Container>
    );
  }

  if (selectedAssessment) {
    const currentQ = selectedAssessment.questions[currentQuestion];
    return (
      <Container>
        <Card style={{ maxWidth: "800px", margin: "0 auto" }}>
          <CardHeader>
            <h2>{selectedAssessment.title}</h2>
            <div>
              Question {currentQuestion + 1} of{" "}
              {selectedAssessment.questions.length}
            </div>
          </CardHeader>

          {renderQuestion(currentQ)}

          <CardFooter>
            <SecondaryButton
              onClick={() =>
                setCurrentQuestion((prev) => Math.max(0, prev - 1))
              }
              disabled={currentQuestion === 0}
            >
              Previous
            </SecondaryButton>

            {currentQuestion === selectedAssessment.questions.length - 1 ? (
              <Button
                onClick={submitAssessment}
                disabled={
                  Object.keys(userAnswers).length <
                  selectedAssessment.questions.length
                }
              >
                Submit Assessment
              </Button>
            ) : (
              <Button
                onClick={() =>
                  setCurrentQuestion((prev) =>
                    Math.min(selectedAssessment.questions.length - 1, prev + 1)
                  )
                }
              >
                Next
              </Button>
            )}
          </CardFooter>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <header style={{ marginBottom: "2rem", textAlign: "center" }}>
        <PageHeader>
          <PageTitle>Assessment Center</PageTitle>
        </PageHeader>
      </header>

      <AssessmentGrid>
        {assessments.map((assessment) => (
          <AssessmentContainer
            key={assessment.id}
            onClick={() => startAssessment(assessment)}
          >
            <AssessmentTitle>{assessment.title}</AssessmentTitle>
            <AssessmentDescription>
              {assessment.description}
            </AssessmentDescription>{" "}
            <AssessmentInfo>
              <MetaInfo>
                <span>⏱️</span>
                <span>{assessment.duration}</span>
              </MetaInfo>
              <MetaInfo>
                <span>❓</span>
                <span>{assessment.totalQuestions} questions</span>
              </MetaInfo>
              {temporaryScores[assessment.id] && (
                <PreviousScore score={temporaryScores[assessment.id].score}>
                  <span>🏆</span>
                  <span>
                    Previous Score: {temporaryScores[assessment.id].score}%
                  </span>
                </PreviousScore>
              )}
            </AssessmentInfo>
            <SkillsSection>
              {assessment.skills.map((skill, index) => (
                <SkillTag key={index}>{skill}</SkillTag>
              ))}
            </SkillsSection>{" "}
            <AssessmentFooter>
              <Button
                style={{
                  width: "100%",
                  height: "40px",
                  marginBottom: temporaryScores[assessment.id] ? "0.5rem" : "0",
                }}
              >
                {temporaryScores[assessment.id]
                  ? "Retake Assessment"
                  : "Start Assessment"}
              </Button>
              {temporaryScores[assessment.id] && (
                <LastTaken>
                  Last taken:{" "}
                  {new Date(
                    temporaryScores[assessment.id].date
                  ).toLocaleDateString()}
                </LastTaken>
              )}
            </AssessmentFooter>
          </AssessmentContainer>
        ))}
      </AssessmentGrid>
    </Container>
  );
};

export default AssessmentCenter;
